import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { Storage } from '../services/storage.service';
import { Auth } from '../services/auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Attaches the JWT Bearer token and user context headers to outgoing requests,
 * and automatically regenerates the token via Refresh Token on 401 Unauthorized.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(Storage);
  const router = inject(Router);
  const auth = inject(Auth);

  const addHeaders = (request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> => {
    let headers: Record<string, string> = {};
    const user = storage.getUser();

    if (request.method === 'GET') {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      headers['Pragma'] = 'no-cache';
      headers['Expires'] = '0';
    }

    if (token) {
      headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    }

    if (user) {
      if (user.id) {
        headers['X-User-Id'] = String(user.id);
      }
      if (user.role) {
        headers['X-User-Role'] = user.role;
      }
    }

    return Object.keys(headers).length > 0
      ? request.clone({ setHeaders: headers })
      : request;
  };

  const initialToken = storage.getToken();
  const cloned = addHeaders(req, initialToken);

  return next(cloned).pipe(
    catchError((error: HttpErrorResponse) => {
      const isAuthEndpoint = req.url.includes('/auth/login') || req.url.includes('/auth/refresh') || req.url.includes('/api/public/') || req.url.includes('/api/career/');

      if (error.status === 401 && !isAuthEndpoint) {
        const refreshToken = storage.getRefreshToken();

        if (refreshToken && !storage.isTokenExpired(refreshToken)) {
          if (!isRefreshing) {
            isRefreshing = true;
            refreshTokenSubject.next(null);

            return auth.refreshToken().pipe(
              switchMap((res) => {
                isRefreshing = false;
                const newToken = res.data?.token || storage.getToken();
                refreshTokenSubject.next(newToken);
                return next(addHeaders(req, newToken));
              }),
              catchError((refreshErr) => {
                isRefreshing = false;
                refreshTokenSubject.next(null);
                auth.logout();
                router.navigate(['/login']);
                return throwError(() => refreshErr);
              })
            );
          } else {
            // Wait until the active refresh completes and then retry request
            return refreshTokenSubject.pipe(
              filter((token): token is string => token !== null),
              take(1),
              switchMap((token) => next(addHeaders(req, token)))
            );
          }
        } else {
          // No valid refresh token available; redirect to login
          auth.logout();
          router.navigate(['/login']);
        }
      }

      return throwError(() => error);
    })
  );
};


